import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastVariants } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../context/analytics-context';
import { useAppContext } from '../context/app-context';
import { hideLoadingScreen, setIsToasterOpen, showLoadingScreen } from '../context/app-context/app-context-actions';
import { useCardContext } from '../context/card-context';
import { useInitializationDataContext } from '../context/data-context';
import { addWamImageToCanvas } from '../components/card-editor/utils/add-wam-image-to-canvas';
import { useHistoryTransaction } from '../features/edit-history/hooks/use-history-transaction';
import { imageConverter } from '../utils/utility/image-converter';
import uploadImage, { MAX_FILE_SIZE } from '../utils/utility/image-upload';
import { InvalidImageFormatError } from '../utils/utility/validate-uploaded-image-format';
import { useActiveCanvas } from './use-active-canvas';
import { useFeatureFlags } from './use-feature-flags';
import { useIsPodProductCode } from './use-is-pod-product-code';
import { useDatadog } from './use-datadog';
import { useS3Upload } from './use-s3-upload';

export type WamStep = {
  title: string;
  text: string;
  imageUrl: string;
};

type UseWamImageUploadParams = {
  onClose: () => void;
  onImageSelect: () => void;
  onCrop: () => void;
};

type UseWamImageUploadResult = {
  steps: WamStep[];
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  buttonRef: React.MutableRefObject<HTMLButtonElement | null>;
  handleImageUpload: (event: React.FormEvent<HTMLInputElement>) => Promise<void>;
};

export const useWamImageUpload = ({
  onClose,
  onImageSelect,
  onCrop,
}: UseWamImageUploadParams): UseWamImageUploadResult => {
  const { t } = useTranslation();
  const datadog = useDatadog();
  const { uploadFile } = useS3Upload();
  const { cardState } = useCardContext();
  const { trackWamPhotoUpload } = useAnalyticsContext();
  const { appDispatch } = useAppContext();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const canvas = useActiveCanvas();
  const isPodProductCode = useIsPodProductCode();
  const { IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED } = useFeatureFlags();
  const runHistoryTransaction = useHistoryTransaction();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const steps = useMemo<WamStep[]>(
    () => [
      {
        title: `${t('writeMessage.stepOneTitle')}`,
        text: `${t('writeMessage.stepOneText')}`,
        imageUrl:
          'https://images.ctfassets.net/ighyyu7ii7g1/600q37bg0hqGvDCJ4Sp3B/ea827a0d85394b05fdc36c65d0e7262e/MGE-sign-and-send-step-01.jpg',
      },
      {
        title: `${t('writeMessage.stepTwoTitle')}`,
        text: `${t('writeMessage.stepTwoText')}`,
        imageUrl:
          'https://images.ctfassets.net/ighyyu7ii7g1/7k3pehUQwZpz7PTiQ2Cwj7/2f982b3e5f8d2e25e1bdf5a7cdffa3f4/MGE-sign-and-send-step-02.jpg',
      },
      {
        title: `${t('writeMessage.stepThreeTitle')}`,
        text: `${t('writeMessage.stepThreeText')}`,
        imageUrl:
          'https://images.ctfassets.net/ighyyu7ii7g1/5se61qUw51OVhvzSN9weXg/cd38161c01ae0960887da595e402eb22/MGE-sign-and-send-step-03.jpg',
      },
    ],
    [t],
  );

  const errorMessage = t('writeMessage.sizeErrorMessage');

  const handleImageUpload = async (event: React.FormEvent<HTMLInputElement>): Promise<void> => {
    const uploadedFiles = (event.target as HTMLInputElement).files;
    if (!uploadedFiles || !initializedData) {
      throw Error('Required data undefined when uploading hadnwriting');
    }

    const fileInfo = Array.from(uploadedFiles ?? []).map((uploadedFile) => {
      return {
        name: uploadedFile.name,
        lastModified: uploadedFile.lastModified,
        size: uploadedFile.size,
        type: uploadedFile.type,
      };
    });

    datadog.addAction('upload-handwriting-photo', {
      files: fileInfo,
      totalUploadedPhotos: fileInfo.length,
      samplePhotoSize: fileInfo[0]?.size,
      samplePhotoType: fileInfo[0]?.type,
      cardFace: cardState.cardFacesList[`${cardState.activeCardIndex}`]?.type ?? null,
      zoneId: cardState.selectedPhotoZone?.id || null,
    });

    const convertedFile = await imageConverter(uploadedFiles[0])();
    const isInvalidSize = convertedFile.size >= MAX_FILE_SIZE;
    if (isInvalidSize) {
      onClose();
      return setIsToasterOpen(appDispatch, {
        variant: ToastVariants.Error,
        title: `${t('writeMessage.sizeTitleError')}`,
        children: errorMessage,
      });
    }

    try {
      showLoadingScreen(appDispatch, 'Uploading your image...');

      const { imageUrl, imageId, cleanUpVersionId } = await uploadImage(
        convertedFile,
        initializedData.project_id,
        uploadFile,
        {
          cleanUp: true,
          uploadMetadata: {
            is_handwriting_image: 'true',
            display_indicator: 'false',
          },
          events: {
            onCleanUpStart: () => showLoadingScreen(appDispatch, 'Making it look handwritten...'),
          },
        },
      );

      const imageAddedSuccessfully = await addWamImageToCanvas(
        imageUrl,
        isPodProductCode,
        cardState,
        canvas,
        imageId,
        cleanUpVersionId,
        onImageSelect,
        onCrop,
        IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
        async (placement) => {
          const targetCanvas = canvas?.current;
          if (!targetCanvas) {
            placement();
            return;
          }
          await runHistoryTransaction(targetCanvas, placement);
        },
      );

      if (imageAddedSuccessfully) {
        trackWamPhotoUpload(convertedFile.size.toString());
        datadog.addAction('add-handwriting-to-card-face', {
          handwriting: imageAddedSuccessfully.data,
          cardFace: cardState.cardFacesList[`${cardState.activeCardIndex}`]?.type ?? null,
          zoneId: cardState.selectedPhotoZone?.id || null,
        });
      }

      hideLoadingScreen(appDispatch);
    } catch (e) {
      if (e instanceof InvalidImageFormatError) {
        setIsToasterOpen(appDispatch, {
          variant: ToastVariants.Error,
          title: t('imageUploadDrawer.imageErrorTitle'),
          children: t('imageUploadDrawer.onlyImagesMessage'),
        });
      } else {
        setIsToasterOpen(appDispatch, {
          title: t('writeMessage.errorTitle'),
          children: errorMessage,
          variant: ToastVariants.Error,
        });
      }

      hideLoadingScreen(appDispatch);
    }
  };

  return { steps, inputRef, buttonRef, handleImageUpload };
};
